import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const api = axios.create({
  baseURL: `${API}/api`,
});

// Request interceptor to add auth token from localStorage
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('access_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Response interceptor for token refresh
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      try {
        const rt = localStorage.getItem('refresh_token');
        const refreshRes = await axios.post(`${API}/api/auth/refresh`, {}, {
          headers: rt ? { 'X-Refresh-Token': rt } : {},
        });
        if (refreshRes.data.access_token) {
          localStorage.setItem('access_token', refreshRes.data.access_token);
          originalRequest.headers.Authorization = `Bearer ${refreshRes.data.access_token}`;
        }
        return api(originalRequest);
      } catch (refreshError) {
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
        return Promise.reject(refreshError);
      }
    }
    return Promise.reject(error);
  }
);

export function formatApiError(error) {
  const detail = error?.response?.data?.detail;
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  }
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

// News API
export const newsApi = {
  getAll: (skip = 0, limit = 20) => api.get(`/news?skip=${skip}&limit=${limit}`),
  getOne: (id) => api.get(`/news/${id}`),
  create: (data) => api.post('/news', data),
  update: (id, data) => api.put(`/news/${id}`, data),
  delete: (id) => api.delete(`/news/${id}`),
  uploadImage: (articleId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/news/${articleId}/upload-image`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  scrape: () => api.post('/news/scrape'),
  scrapeStatus: () => api.get('/news/scrape-status'),
  count: () => api.get('/news/count'),
  adminList: () => api.get('/admin/news'),
  like: (id) => api.post(`/news/${id}/like`),
  dislike: (id) => api.post(`/news/${id}/dislike`),
  getMyReaction: (id) => api.get(`/news/${id}/my-reaction`),
  reorder: (articleIds) => api.post('/news/reorder', { article_ids: articleIds }),
};

// Forum API
export const forumApi = {
  getCategories: () => api.get('/forum/categories'),
  getRaceWinsPhotos: (limit = 20) => api.get(`/forum/race-wins-photos?limit=${limit}`),
  getThreads: (categoryId, skip = 0, limit = 20) => {
    const params = new URLSearchParams({ skip, limit });
    if (categoryId) params.append('category_id', categoryId);
    return api.get(`/forum/threads?${params}`);
  },
  getThread: (id) => api.get(`/forum/threads/${id}`),
  createThread: (data) => api.post('/forum/threads', data),
  getPosts: (threadId, skip = 0, limit = 50) => api.get(`/forum/posts/${threadId}?skip=${skip}&limit=${limit}`),
  createPost: (data) => api.post('/forum/posts', data),
  deleteThread: (id) => api.delete(`/forum/threads/${id}`),
  deletePost: (id) => api.delete(`/forum/posts/${id}`),
  pinThread: (id) => api.put(`/forum/threads/${id}/pin`),
  lockThread: (id) => api.put(`/forum/threads/${id}/lock`),
  hideThread: (id) => api.put(`/forum/threads/${id}/hide`),
  editThread: (id, data) => api.put(`/forum/threads/${id}/edit`, data),
  editPost: (id, data) => api.put(`/forum/posts/${id}/edit`, data),
  hidePost: (id) => api.put(`/forum/posts/${id}/hide`),
  react: (targetType, targetId, reaction) => api.post(`/forum/react/${targetType}/${targetId}`, { reaction }),
  getReactions: (targetType, targetId) => api.get(`/forum/reactions/${targetType}/${targetId}`),
  uploadImage: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/forum/upload-image', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
};

// Listings API
export const listingsApi = {
  getAll: (filters = {}) => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params.append(key, value);
      }
    });
    return api.get(`/listings?${params}`);
  },
  getOne: (id) => api.get(`/listings/${id}`),
  create: (data) => api.post('/listings', data),
  update: (id, data) => api.put(`/listings/${id}`, data),
  delete: (id) => api.delete(`/listings/${id}`),
  getMine: () => api.get('/my-listings'),
  uploadImage: (listingId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/upload/listing-image/${listingId}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  deleteImage: (listingId, imageIndex) =>
    api.delete(`/listings/${listingId}/images/${imageIndex}`),
};

// Driver of the Week API
export const dotwApi = {
  getAll: (skip = 0, limit = 50, status, week) => {
    const params = new URLSearchParams({ skip, limit });
    if (status) params.append('status', status);
    if (week) params.append('week', week);
    return api.get(`/driver-of-the-week?${params}`);
  },
  getCurrentWeek: () => api.get('/driver-of-the-week/current-week'),
  getWeeks: (skip = 0, limit = 20) => api.get(`/driver-of-the-week/weeks?skip=${skip}&limit=${limit}`),
  getOne: (id) => api.get(`/driver-of-the-week/${id}`),
  create: (data) => api.post('/driver-of-the-week', data),
  submit: (data) => api.post('/driver-of-the-week/submit', data),
  update: (id, data) => api.put(`/driver-of-the-week/${id}`, data),
  delete: (id) => api.delete(`/driver-of-the-week/${id}`),
  uploadImage: (id, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/driver-of-the-week/${id}/upload-image`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
};

export const profileApi = {
  get: (userId) => api.get(`/users/${userId}/profile`),
  updateMe: (data) => api.put('/users/me/profile', data),
  uploadAvatar: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/users/me/avatar', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  deleteAvatar: () => api.delete('/users/me/avatar'),
  adminUpdate: (userId, data) => api.put(`/admin/users/${userId}/profile`, data),
  adminToggleDisabled: (userId) => api.put(`/admin/users/${userId}/profile-disabled`),
};

export const interviewsApi = {
  getFeatured: () => api.get('/interviews/featured'),
  getAll: () => api.get('/interviews'),
  get: (slug) => api.get(`/interviews/${slug}`),
  adminList: () => api.get('/admin/interviews'),
  create: (data) => api.post('/admin/interviews', data),
  update: (id, data) => api.put(`/admin/interviews/${id}`, data),
  delete: (id) => api.delete(`/admin/interviews/${id}`),
  uploadImage: (id, kind, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/admin/interviews/${id}/upload-image?kind=${kind}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
};

export const sponsorsApi = {
  list: () => api.get('/sponsors'),
  adminList: () => api.get('/admin/sponsors'),
  create: (data) => api.post('/admin/sponsors', data),
  update: (id, data) => api.put(`/admin/sponsors/${id}`, data),
  delete: (id) => api.delete(`/admin/sponsors/${id}`),
  uploadLogo: (id, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/admin/sponsors/${id}/upload-logo`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
};

export const getFileUrl = (path) => {
  if (!path) return '';
  if (path.startsWith('/api/files/')) return `${API}${path}`;
  if (path.startsWith('http')) return path;
  return `${API}/api/files/${path}`;
};

// Resolve any image URL - handles relative /api/files/ paths and absolute URLs
export const resolveImageUrl = (url) => {
  if (!url) return '';
  if (url.startsWith('/api/files/')) return `${API}${url}`;
  return url;
};

export default api;
